import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import Breadcrumb from '../components/Breadcrumb';
import FeatureNotEnabled from '../components/FeatureNotEnabled';
import { featureFlagsService } from '../services/api';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../components/ui/card';
import { 
  BarChart3, 
  AlertCircle,
  TrendingUp,
  Clock,
  Users,
  DollarSign,
  Target
} from 'lucide-react';

/**
 * Advanced Analytics Module (Phase 6 - Scaffolded)
 * 
 * FEATURE FLAG: advanced_analytics_enabled
 * ACCESS: Super Admin + Admin only
 * STATUS: Placeholder - No forecasting, read-only tiles
 */
const AdvancedAnalyticsPage = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [featureEnabled, setFeatureEnabled] = useState(false);
  const [loading, setLoading] = useState(true);

  const allowedRoles = ['admin', 'super_admin', 'ops_admin'];

  useEffect(() => {
    if (user && !allowedRoles.includes(user.role)) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  useEffect(() => {
    const checkFeature = async () => {
      try {
        const enabledFeatures = await featureFlagsService.getEnabledFeatures();
        setFeatureEnabled(enabledFeatures.includes('advanced_analytics_enabled'));
      } catch (err) {
        console.error('Failed to check feature flag:', err);
        setFeatureEnabled(false);
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      checkFeature();
    }
  }, [token]);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (!featureEnabled) {
    return <FeatureNotEnabled featureName="Advanced Analytics" />;
  }

  // Placeholder content when feature IS enabled (future-ready scaffold)
  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto p-4 md:p-6">
        {/* Breadcrumb */}
        <Breadcrumb items={[{ label: 'Advanced Analytics' }]} />

        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 flex items-center gap-2">
            <BarChart3 className="w-8 h-8 text-indigo-600" />
            Advanced Analytics
          </h1>
          <p className="text-gray-600 mt-1">Business intelligence and performance metrics</p>
        </div>

        {/* Data Notice */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold text-blue-800">Analytics will populate after operations begin</div>
            <div className="text-sm text-blue-700">
              This is a read-only preview interface. Data shown is placeholder only. 
              Actual metrics will be available once dispatch operations are active.
            </div>
          </div>
        </div>

        {/* Placeholder Metric Tiles */}
        <div className="grid md:grid-cols-3 gap-6 mb-6">
          {/* Revenue Tile */}
          <Card className="border-dashed border-2 border-gray-300 bg-gray-50">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-gray-500 text-lg">
                <DollarSign className="w-5 h-5" />
                Revenue
              </CardTitle>
              <CardDescription>No data</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-gray-300">$--,---</div>
              <div className="text-sm text-gray-400 mt-2 flex items-center gap-1">
                <TrendingUp className="w-4 h-4" />
                <span>--% vs last period</span>
              </div>
              <div className="mt-4 h-16 bg-gray-200 rounded animate-pulse"></div>
            </CardContent>
          </Card>

          {/* On-Time % Tile */}
          <Card className="border-dashed border-2 border-gray-300 bg-gray-50">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-gray-500 text-lg">
                <Target className="w-5 h-5" />
                On-Time %
              </CardTitle>
              <CardDescription>No data</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-gray-300">--%</div>
              <div className="text-sm text-gray-400 mt-2 flex items-center gap-1">
                <Clock className="w-4 h-4" />
                <span>-- deliveries tracked</span>
              </div>
              <div className="mt-4 h-16 bg-gray-200 rounded animate-pulse"></div>
            </CardContent>
          </Card>

          {/* Driver Retention Tile */}
          <Card className="border-dashed border-2 border-gray-300 bg-gray-50">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-gray-500 text-lg">
                <Users className="w-5 h-5" />
                Driver Retention
              </CardTitle>
              <CardDescription>No data</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-gray-300">--%</div>
              <div className="text-sm text-gray-400 mt-2 flex items-center gap-1">
                <TrendingUp className="w-4 h-4" />
                <span>-- active drivers</span>
              </div>
              <div className="mt-4 h-16 bg-gray-200 rounded animate-pulse"></div>
            </CardContent>
          </Card>
        </div>

        {/* Placeholder Chart Area */}
        <Card className="border-dashed border-2 border-gray-300 bg-gray-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-gray-500">
              <BarChart3 className="w-5 h-5" />
              Performance Trends
            </CardTitle>
            <CardDescription>Historical performance data will appear here</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-64 flex items-center justify-center">
              <div className="text-center text-gray-400">
                <BarChart3 className="w-16 h-16 mx-auto mb-4 opacity-30" />
                <p className="font-medium">No data available</p>
                <p className="text-sm mt-1">Charts will populate once operations data is collected</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default AdvancedAnalyticsPage;
